import React, { useCallback } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Paper, Typography } from '@mui/material';

// Pages
import HomePage from '../HomePage.js';
import Recipes from '../Recipes.js';
import IngredientsList from '../IngredientsList.js';
import IngredientDetail from '../IngredientDetail.js';
import CocktailMenus from '../CocktailMenus.js';
import CocktailMenuDetail from '../CocktailMenuDetail.js';
import AddRecipe from '../AddRecipe.js';
import MyRecipes from '../MyRecipes.js';
import Login from '../Login.js';
import SignUp from '../SignUp.js';
import RecipeDetail from '../RecipeDetail.js';
import IngredientsPage from '../pages/IngredientsPage.js';

// Contexts
import { useUser } from '../contexts/UserContext.js';
import { useRecipes } from '../contexts/recipe/RecipeContext.js';
import { useInventory } from '../contexts/InventoryContext.js';
import { useMenus } from '../contexts/MenuContext.js';

const PrivateRoute = ({ children }) => {
  const { user, hasAccess } = useUser();
  
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAccess) return <Navigate to="/pending-access" replace />;
  return children;
};

// Recipe detail helper for origin filtering
function RecipeDetailWithOriginFilter(props) {
  const { origin, recipeId } = useParams();
  const { user } = useUser();
  const { 
    recipes, 
    favorites, 
    bookmarks, 
    toggleFavorite, 
    toggleBookmark,
    handleRecipeDeleted 
  } = useRecipes();
  const { inventory, togglePossession } = useInventory();
  const { menus, addRecipeToMenu, createMenu } = useMenus();
  
  // If recipeId is present, we're viewing a recipe detail
  if (recipeId) {
    return (
      <RecipeDetail
        recipes={recipes}
        inventory={inventory}
        togglePossession={togglePossession}
        favorites={favorites}
        bookmarks={bookmarks}
        toggleFavorite={toggleFavorite}
        toggleBookmark={toggleBookmark}
        menus={menus}
        onAddRecipeToMenu={addRecipeToMenu}
        onCreateMenu={createMenu}
        user={user}
        onRecipeDeleted={handleRecipeDeleted}
        defaultMeasurementSystem={props.unitPreference}
        scrollRef={props.scrollRef}
      />
    );
  }
  
  // Otherwise, we're viewing a collection
  const decodedOrigin = origin ? decodeURIComponent(origin) : null;
  
  return (
    <>
      <Recipes
        recipes={recipes}
        inventory={inventory}
        togglePossession={togglePossession}
        favorites={favorites}
        bookmarks={bookmarks}
        toggleFavorite={toggleFavorite}
        toggleBookmark={toggleBookmark}
        originFilter={decodedOrigin}
      />
    </>
  );
}

// Edit recipe wrapper
function EditRecipeWrapper() {
  const { recipeId } = useParams();
  const { recipes } = useRecipes();
  const { user } = useUser();
  const { inventory, refreshIngredients, refreshInventory } = useInventory();
  const { handleRecipeUpdated } = useRecipes();
  
  const recipe = recipes.find(r => r.id === recipeId);

  const handleIngredientAdded = async () => {
    await refreshIngredients();
    await refreshInventory();
  };

  return (
    <AddRecipe 
      user={user} 
      inventory={inventory} 
      onIngredientAdded={handleIngredientAdded}
      onRecipeUpdated={handleRecipeUpdated}
      recipe={recipe}
    />
  );
}

const AppRouter = ({ scrollRef, unitPreference, resetSearchFilters }) => {
  const { user, hasAccess, handleLogin, handleSignUp } = useUser();
  const { 
    recipes, 
    favorites, 
    bookmarks, 
    toggleFavorite, 
    toggleBookmark,
    handleRecipeAdded,
    handleRecipeDeleted,
    handleRecipeUpdated
  } = useRecipes();
  const { inventory, togglePossession, refreshIngredients, refreshInventory } = useInventory();
  const { menus, createMenu, addRecipeToMenu, removeRecipeFromMenu, deleteMenu, renameMenu } = useMenus();
  
  // Create a callback that refreshes ingredients and inventory
  const handleIngredientAdded = useCallback(async () => {
    await refreshIngredients();
    await refreshInventory();
  }, [refreshIngredients, refreshInventory]);
  
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <>
            <Helmet>
              <title>Trove | Login</title>
            </Helmet>
            <Login onLogin={handleLogin} />
          </>
        }
      />
      <Route
        path="/signup"
        element={
          <>
            <Helmet>
              <title>Trove | Sign Up</title>
            </Helmet>
            <SignUp onSignUp={handleSignUp} />
          </>
        }
      />
      <Route
        path="/pending-access"
        element={
          user && !hasAccess ? (
            <>
              <Helmet>
                <title>Trove | Access Pending</title>
              </Helmet>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
                  <Typography variant="h5" gutterBottom align="center">
                    Access Pending
                  </Typography>
                  <Typography align="center">
                    Your account is awaiting approval. Please wait for an admin to grant access.
                  </Typography>
                </Paper>
              </Box>
            </>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <HomePage 
              recipes={recipes} 
              inventory={inventory} 
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
              resetSearchFilters={resetSearchFilters}
              unitPreference={unitPreference}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <PrivateRoute>
            <Recipes
              recipes={recipes}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/all"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | All Recipes</title>
            </Helmet>
            <Recipes
              recipes={recipes}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/can-make"
        element={
          <PrivateRoute>
            <Recipes
              recipes={recipes.filter(recipe => {
                const requiredIngredients = recipe.ingredients || [];
                const requiredIds = new Set(requiredIngredients.map(ing => ing.ingredientId || ing.id));
                const userIngredientIds = new Set(inventory.filter(ing => ing.possessed).map(ing => ing.id));
                return [...requiredIds].every(id => userIngredientIds.has(id));
              })}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/missing-one"
        element={
          <PrivateRoute>
            <Recipes
              recipes={recipes.filter(recipe => {
                const requiredIngredients = recipe.ingredients || [];
                const requiredIds = new Set(requiredIngredients.map(ing => ing.ingredientId || ing.id));
                const userIngredientIds = new Set(inventory.filter(ing => ing.possessed).map(ing => ing.id));
                const missingCount = [...requiredIds].filter(id => !userIngredientIds.has(id)).length;
                return missingCount === 1;
              })}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/missing-two"
        element={
          <PrivateRoute>
            <Recipes
              recipes={recipes.filter(recipe => {
                const requiredIngredients = recipe.ingredients || [];
                const requiredIds = new Set(requiredIngredients.map(ing => ing.ingredientId || ing.id));
                const userIngredientIds = new Set(inventory.filter(ing => ing.possessed).map(ing => ing.id));
                const missingCount = [...requiredIds].filter(id => !userIngredientIds.has(id)).length;
                return missingCount === 2;
              })}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/favorites"
        element={
          <PrivateRoute>
            <Recipes
              recipes={recipes.filter(recipe => favorites.has(recipe.id))}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/bookmarks"
        element={
          <PrivateRoute>
            <Recipes
              recipes={recipes.filter(recipe => bookmarks.has(recipe.id))}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/collection/:origin"
        element={
          <PrivateRoute>
            <RecipeDetailWithOriginFilter
              unitPreference={unitPreference}
              scrollRef={scrollRef}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipes/my-recipes"
        element={
          <PrivateRoute>
            <MyRecipes
              user={user}
              recipes={recipes}
              inventory={inventory}
              togglePossession={togglePossession}
              favorites={favorites}
              bookmarks={bookmarks}
              toggleFavorite={toggleFavorite}
              toggleBookmark={toggleBookmark}
              handleSignUp={handleSignUp}
              handleRecipeAdded={handleRecipeAdded}
              handleRecipeDeleted={handleRecipeDeleted}
              handleRecipeUpdated={handleRecipeUpdated}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/ingredients"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Ingredients</title>
            </Helmet>
            <IngredientsPage
              recipes={recipes}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipe/:recipeId"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Recipe Detail</title>
            </Helmet>
            <RecipeDetailWithOriginFilter
              unitPreference={unitPreference}
              scrollRef={scrollRef}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/ingredient/:ingredientId"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Ingredient Detail</title>
            </Helmet>
            <IngredientDetail
              inventory={inventory}
              recipes={recipes}
              togglePossession={togglePossession}
              ingredients={inventory}
              user={user}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/cocktail-menus"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Cocktail Menus</title>
            </Helmet>
            <CocktailMenus menus={menus} onCreateMenu={createMenu} />
          </PrivateRoute>
        }
      />
      <Route
        path="/cocktail-menu/:menuId"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Cocktail Menu Detail</title>
            </Helmet>
            <CocktailMenuDetail
              menus={menus}
              recipes={recipes}
              inventory={inventory}
              onAddRecipe={addRecipeToMenu}
              onRemoveRecipe={removeRecipeFromMenu}
              onDeleteMenu={deleteMenu}
              onRenameMenu={renameMenu}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipe/new"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Add Recipe</title>
            </Helmet>
            <AddRecipe 
              user={user} 
              inventory={inventory} 
              onIngredientAdded={handleIngredientAdded}
              onRecipeAdded={handleRecipeAdded}
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/recipe/:recipeId/edit"
        element={
          <PrivateRoute>
            <Helmet>
              <title>Trove | Edit Recipe</title>
            </Helmet>
            <EditRecipeWrapper />
          </PrivateRoute>
        }
      />
      <Route
        path="*"
        element={
          <>
            <Helmet>
              <title>Trove | Not Found</title>
            </Helmet>
            <Navigate to="/login" replace />
          </>
        }
      />
    </Routes>
  );
};

export default AppRouter; 